import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { alertApi, type AlertHolidayRow } from '@/api/alert.api';

const { Title, Text } = Typography;

export function AlertHolidaysPage() {
  const queryClient = useQueryClient();
  const thisYear = dayjs().year();
  const [year, setYear] = useState<number>(thisYear);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [name, setName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['alert-holidays', year],
    queryFn: () => alertApi.holidays(year),
  });

  const addMutation = useMutation({
    mutationFn: () => alertApi.addHoliday(date!.format('YYYY-MM-DD'), name.trim()),
    onSuccess: () => {
      message.success('공휴일이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['alert-holidays'] });
      setDate(null);
      setName('');
    },
    onError: () => {
      message.error('저장 중 오류가 발생했습니다.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertApi.deleteHoliday(id),
    onSuccess: () => {
      message.success('공휴일이 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['alert-holidays'] });
    },
    onError: () => {
      message.error('삭제 중 오류가 발생했습니다.');
    },
  });

  const handleAdd = () => {
    if (!date || !name.trim()) {
      message.warning('날짜와 이름을 모두 입력하세요.');
      return;
    }
    addMutation.mutate();
  };

  const rows = data ?? [];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          알람 앱 공휴일
        </Title>
        <Segmented
          value={year}
          onChange={(v) => setYear(v as number)}
          options={[
            { label: `올해 (${thisYear})`, value: thisYear },
            { label: `내년 (${thisYear + 1})`, value: thisYear + 1 },
          ]}
        />
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <DatePicker
            value={date}
            onChange={setDate}
            placeholder="날짜 선택"
            format="YYYY-MM-DD"
          />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={handleAdd}
            placeholder="공휴일 이름 (예: 개천절)"
            maxLength={40}
            style={{ width: 220 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={addMutation.isPending}
            onClick={handleAdd}
          >
            추가
          </Button>
          <Text type="secondary">같은 날짜를 다시 추가하면 이름이 갱신됩니다.</Text>
        </Space>
      </Card>

      {!isLoading && rows.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="비워두면 앱 내장 목록(2026~2028)이 쓰입니다."
          style={{ marginBottom: 16 }}
        />
      )}

      <Card title={`${year}년 공휴일 ${rows.length}건`}>
        <Table<AlertHolidayRow>
          size="small"
          rowKey="id"
          loading={isLoading}
          pagination={false}
          dataSource={rows}
          columns={[
            { title: '날짜', dataIndex: 'date', width: 140 },
            {
              title: '요일',
              dataIndex: 'date',
              key: 'weekday',
              width: 80,
              render: (v: string) => dayjs(v).format('ddd'),
            },
            { title: '이름', dataIndex: 'name' },
            {
              title: '',
              key: 'action',
              width: 80,
              render: (_, record) => (
                <Popconfirm
                  title="이 공휴일을 삭제할까요?"
                  okText="삭제"
                  cancelText="취소"
                  onConfirm={() => deleteMutation.mutate(record.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
